import {AuthorityType, PaymentMethod, TransferType} from '@sollapay/enums';
import {REFERENCE_CODE_TOTAL_LENGTH} from '@sollapay/utils';

import type {PayoutTransferType} from '@sollapay/enums';

/**
 * Hebrew labels for each authority type. This is always the creditor's stored `name` — the
 * specific instance (e.g. which municipality) lives in its own `municipalityName` field instead,
 * so consumers reading `name` never need to branch on kind for the generic label.
 */
const FIXED_AUTHORITY_NAMES: Readonly<Record<string, string>> = {
  [AuthorityType.MUNICIPALITY]: 'רשות מקומית',
  [AuthorityType.TAX_AUTHORITY]: 'רשות המסים',
  [AuthorityType.ISRAEL_LAND_AUTHORITY]: 'רשות מקרקעי ישראל'
};

/** Separator between our reference code and the authority's own reference in a transfer description. */
const DESCRIPTION_SEPARATOR = ' ';

/** The generic creditor name stored on every authority payout, regardless of type. */
export function resolveAuthorityCreditorName(authorityType: AuthorityType): string {
  const name = FIXED_AUTHORITY_NAMES[authorityType];
  if (!name) throw new Error(`No canonical name for authority type ${authorityType}`);
  return name;
}

/**
 * Both identifiers have to survive in the bank's one description field: the authority matches the
 * bill on theirs, and StatementDebitMatcher settles the debit by extracting ours.
 */
export function buildAuthorityTransferDescription(params: {
  referenceCode: string;
  paymentReference: string;
}): string {
  return `${params.referenceCode}${DESCRIPTION_SEPARATOR}${params.paymentReference.trim()}`;
}

/** Longest authority reference that still leaves room for our reference code in the description. */
export function maxPisPaymentReferenceLength(maxDescriptionLength: number): number {
  return maxDescriptionLength - REFERENCE_CODE_TOTAL_LENGTH - DESCRIPTION_SEPARATOR.length;
}

/**
 * Whether an authority payout can go out on the provider rail. A longer reference would be
 * silently truncated by the provider, corrupting one of the two identifiers, so those payouts
 * are paid manually instead. Reference codes are fixed-length, so this holds before one is generated.
 */
export function isPisEligiblePaymentReference(params: {
  paymentReference: string;
  maxDescriptionLength: number;
}): boolean {
  const {paymentReference, maxDescriptionLength} = params;
  return paymentReference.trim().length <= maxPisPaymentReferenceLength(maxDescriptionLength);
}

/**
 * Tax authority payouts have no creditor account at all, and an over-long authority reference
 * can't be sent safely — both are paid by hand instead of through the provider.
 */
export function resolveAuthorityInitiationMethod(params: {
  authorityType: AuthorityType;
  paymentReference?: string | null;
  maxDescriptionLength: number;
}): PaymentMethod {
  const {authorityType, paymentReference, maxDescriptionLength} = params;

  if (authorityType === AuthorityType.TAX_AUTHORITY) return PaymentMethod.MANUAL;

  if (paymentReference && isPisEligiblePaymentReference({paymentReference, maxDescriptionLength})) {
    return PaymentMethod.PIS;
  }

  return PaymentMethod.MANUAL;
}

/** The Tax Authority is paid against its voucher; every other authority's rail is the lawyer's choice. */
export function resolveAuthorityTransferType(params: {
  authorityType: AuthorityType;
  transferType?: PayoutTransferType;
}): PayoutTransferType | null {
  if (params.authorityType === AuthorityType.TAX_AUTHORITY) return TransferType.VOUCHER;

  return params.transferType ?? null;
}
